import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { 
  ArrowUpCircle, ArrowDownCircle, DollarSign, TrendingUp, 
  RefreshCw, Filter, Download, Trophy, XCircle, Percent
} from 'lucide-react';

const TransactionsTab = () => {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [days, setDays] = useState(30);

  // Frontend-Filter -> backend transaction_type. wins/losses sind
  // Subtypen von settlement; serverseitig holen wir alle settlements und
  // verfeinern unten client-seitig. So greift das Limit innerhalb des
  // gewählten Typs (sonst fielen z.B. ältere Commissions aus dem
  // limitierten Gesamt-Stream raus).
  const backendType = (
    filter === 'commissions' ? 'commissions' :
    filter === 'fees' ? 'fees' :
    (filter === 'settlements' || filter === 'wins' || filter === 'losses') ? 'settlement' :
    'all'
  );

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const response = await api.admin.getTransactions({ days, limit: 200, transaction_type: backendType });
      setTransactions(response.data.transactions);
      setSummary(response.data.summary);
    } catch (error) {
      toast.error('Failed to load transactions');
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [days, filter]);

  const getTransactionIcon = (type, subtype) => {
    switch (type) {
      case 'settlement':
        return subtype === 'win_payout' 
          ? <Trophy className="h-4 w-4 text-green-600" />
          : <XCircle className="h-4 w-4 text-red-600 dark:text-rose-400" />;
      case 'fee':
        return <Percent className="h-4 w-4 text-purple-600 dark:text-purple-400" />;
      case 'commission':
        return <DollarSign className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />;
      case 'wallet':
        return subtype === 'win' || subtype === 'credit'
          ? <ArrowUpCircle className="h-4 w-4 text-green-600" />
          : <ArrowDownCircle className="h-4 w-4 text-red-600 dark:text-rose-400" />;
      default:
        return <DollarSign className="h-4 w-4 text-gray-600 dark:text-gray-400" />;
    }
  };

  const getTransactionBadge = (type, subtype) => {
    const variants = {
      'settlement-win_payout': { color: 'bg-green-100 text-green-800', label: 'Win Payout' },
      'settlement-loss': { color: 'bg-red-100 text-red-800', label: 'Loss' },
      'fee-trading': { color: 'bg-purple-100 text-purple-800', label: 'Trading Fee' },
      'fee-settlement': { color: 'bg-purple-100 text-purple-800', label: 'Settlement Fee' },
      'fee-platform': { color: 'bg-purple-100 text-purple-800', label: 'Platform Fee' },
      'commission-creator': { color: 'bg-yellow-100 text-yellow-800', label: 'Creator Commission' },
      'commission-referral_l1': { color: 'bg-yellow-100 text-yellow-800', label: 'Referral L1' },
      'commission-referral_l2': { color: 'bg-yellow-100 text-yellow-800', label: 'Referral L2' },
      'wallet-win': { color: 'bg-green-100 text-green-800', label: 'Credit Win' },
      'wallet-credit': { color: 'bg-blue-100 text-blue-800', label: 'Credit Added' },
      'wallet-debit': { color: 'bg-orange-100 text-orange-800', label: 'Debit' },
      'wallet-bet': { color: 'bg-blue-100 text-blue-800', label: 'Bet Placed' },
    };
    
    const key = `${type}-${subtype}`;
    const variant = variants[key] || { color: 'bg-gray-100 text-gray-800', label: subtype || type };
    
    return (
      <Badge className={`${variant.color} text-xs`}>
        {variant.label}
      </Badge>
    );
  };

  const filteredTransactions = transactions.filter(tx => {
    if (filter === 'all') return true;
    if (filter === 'settlements') return tx.type === 'settlement';
    if (filter === 'fees') return tx.type === 'fee';
    if (filter === 'commissions') return tx.type === 'commission';
    if (filter === 'wins') return tx.type === 'settlement' && tx.subtype === 'win_payout';
    if (filter === 'losses') return tx.type === 'settlement' && tx.subtype === 'loss';
    return true;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-green-50 border-green-200 dark:bg-emerald-500/10">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700">Total Payouts</p>
                  <p className="text-2xl font-bold text-green-600">
                    {summary.total_payouts?.toLocaleString() || 0}
                  </p>
                </div>
                <Trophy className="h-8 w-8 text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-red-50 border-red-200 dark:bg-rose-500/10 dark:border-rose-500/30">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-700 dark:text-rose-400">User Losses</p>
                  <p className="text-2xl font-bold text-red-600 dark:text-rose-400">
                    {summary.total_user_losses?.toLocaleString() || 0}
                  </p>
                </div>
                <XCircle className="h-8 w-8 text-red-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-purple-50 border-purple-200 dark:bg-purple-500/10">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-700">Fees Collected</p>
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {summary.total_fees_collected?.toLocaleString() || 0}
                  </p>
                </div>
                <Percent className="h-8 w-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-yellow-50 border-yellow-200 dark:bg-amber-500/10">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-700">Commissions</p>
                  <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                    {summary.total_commissions_paid?.toLocaleString() || 0}
                  </p>
                </div>
                <DollarSign className="h-8 w-8 text-yellow-400" />
              </div>
            </CardContent>
          </Card>

          <Card className={`${summary.net_platform_revenue >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${summary.net_platform_revenue >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    Net Revenue
                  </p>
                  <p className={`text-2xl font-bold ${summary.net_platform_revenue >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {summary.net_platform_revenue?.toLocaleString() || 0}
                  </p>
                </div>
                <TrendingUp className={`h-8 w-8 ${summary.net_platform_revenue >= 0 ? 'text-emerald-400' : 'text-red-400'}`} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters and Controls */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Platform Transactions
              </CardTitle>
              <CardDescription>
                All credit movements including settlements, fees, and commissions
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select
                className="px-3 py-2 border rounded-md text-sm"
                value={days}
                onChange={(e) => setDays(parseInt(e.target.value))}
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
                <option value={365}>Last year</option>
              </select>
              
              <select
                className="px-3 py-2 border rounded-md text-sm"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                <option value="all">All Transactions</option>
                <option value="settlements">Settlements Only</option>
                <option value="wins">Wins Only</option>
                <option value="losses">Losses Only</option>
                <option value="fees">Fees Only</option>
                <option value="commissions">Commissions Only</option>
              </select>
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={fetchTransactions}
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="h-8 w-8 text-gray-400 mx-auto animate-spin dark:text-gray-500" />
              <p className="text-gray-500 mt-2 dark:text-gray-400">Loading transactions...</p>
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">No transactions found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50 dark:bg-slate-800/60">
                    <th className="p-3 text-left font-medium">Date</th>
                    <th className="p-3 text-left font-medium">Type</th>
                    <th className="p-3 text-left font-medium">User</th>
                    <th className="p-3 text-left font-medium">Description</th>
                    <th className="p-3 text-right font-medium">Amount</th>
                    <th className="p-3 text-left font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((tx, index) => (
                    <tr key={tx.id || index} className="border-b hover:bg-gray-50 dark:hover:bg-slate-800/60">
                      <td className="p-3 text-gray-600 whitespace-nowrap dark:text-gray-400">
                        {formatDate(tx.created_at)}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {getTransactionIcon(tx.type, tx.subtype)}
                          {getTransactionBadge(tx.type, tx.subtype)}
                        </div>
                      </td>
                      <td className="p-3">
                        <div>
                          <div className="font-medium">{tx.user_handle}</div>
                          <div className="text-xs text-gray-500 truncate max-w-[100px] dark:text-gray-400">
                            {tx.user_id?.slice(0, 8)}...
                          </div>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="max-w-[250px] truncate" title={tx.description}>
                          {tx.description}
                        </div>
                        {tx.market_title && (
                          <div className="text-xs text-gray-500 truncate max-w-[250px] dark:text-gray-400">
                            Market: {tx.market_title}
                          </div>
                        )}
                      </td>
                      <td className={`p-3 text-right font-medium whitespace-nowrap ${
                        tx.amount >= 0 
                          ? tx.type === 'fee' ? 'text-purple-600' : 'text-green-600'
                          : 'text-red-600'
                      }`}>
                        {tx.amount >= 0 ? '+' : ''}{tx.amount?.toLocaleString()} credits
                        {tx.stake && tx.type === 'settlement' && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Stake: {tx.stake?.toLocaleString()}
                          </div>
                        )}
                      </td>
                      <td className="p-3">
                        <Badge variant={
                          tx.status === 'paid' || tx.status === 'collected' || tx.status === 'success' 
                            ? 'default' 
                            : tx.status === 'settled' 
                              ? 'secondary' 
                              : 'outline'
                        }>
                          {tx.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {!loading && filteredTransactions.length > 0 && (
            <div className="mt-4 text-sm text-gray-500 text-center dark:text-gray-400">
              Showing {filteredTransactions.length} of {transactions.length} transactions
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TransactionsTab;
